import { encodeAbiParameters, parseAbiParameters } from 'viem'
import { Call } from '../../../utils/multicall'
import { isFailedCall } from '../../../utils/multicall'
import {
  getPositionLensFor,
  LENS_FAMILY,
  PositionLensAbi,
} from '../position-lens'
import { getCompoundV3CometAddress } from '../../public-data/addresses/compoundV3'

/** `uint256 baseSupply` + `uint256 baseBorrow` sit ahead of the row count. */
const HEADER_BYTES = 64
/** `uint16 index` + `uint128 balance`. @see `PositionLens.comet` */
const ROW_BYTES = 18

/**
 * True when this lender's Comet reads on this chain go through `PositionLens`.
 *
 * KEYED ON THE LENDER, NOT JUST THE CHAIN. The walk passes an unresolved Comet
 * address straight into a call, where it fails as one bad multicall entry among
 * many; the lens path has to ABI-encode it, and `encodeAbiParameters` throws on
 * `undefined` — taking the whole request down rather than one lender with it.
 *
 * So every input is resolved here first, and anything missing falls back to the
 * walk. Same rule as `usesAaveLens`, and it is the second time a family has
 * needed it: metadata that is good enough for the walk is NOT automatically good
 * enough for the lens, because the walk tolerates a hole and the encoder does not.
 */
export const usesCometLens = (chainId: string, lender: string): boolean =>
  getPositionLensFor(chainId, LENS_FAMILY.COMET) !== undefined &&
  !!getCompoundV3CometAddress(chainId, lender)

/**
 * ONE call for the whole Comet instead of one `userCollateral` per collateral
 * plus the two base legs.
 *
 * The rewards read is NOT folded in — it lives on a different contract
 * (`CometRewards`) and is unconditional, so it stays a second call. Two calls
 * total against `collaterals + 3`.
 *
 * A Comet is CROSS-MARGIN — one base debt against N collaterals — so the lens
 * emits the base supply and borrow legs UNCONDITIONALLY and only the collateral
 * loop skips. That matters: a caller has to be able to tell "this account is flat"
 * from "this family declined", and a gated base pair would make those identical.
 */
export const buildCometLensCall = (
  chainId: string,
  comet: string | undefined,
  collaterals: string[],
  account: string,
): Call[] => {
  const lens = getPositionLensFor(chainId, LENS_FAMILY.COMET)
  // Unreachable via `usesCometLens`; kept as a hard stop so a caller that skips
  // the predicate gets the walk rather than a payload with a hole in it.
  if (!lens || !comet || collaterals.some((c) => !c)) return []
  const payload = encodeAbiParameters(
    parseAbiParameters('address, address[]'),
    [comet as `0x${string}`, collaterals as `0x${string}`[]],
  )
  return [
    {
      address: lens,
      name: 'getUserData',
      params: [[{ family: LENS_FAMILY.COMET, payload }], account],
      abi: PositionLensAbi as any,
    },
  ]
}

/**
 * Rebuild the per-collateral `data` array the walk would have produced.
 *
 * A RECONSTRUCTION rather than a second converter, for the same reason as Aave:
 * the parser indexes fixed slots (`data[i][0]` for a collateral's balance,
 * `data[n]` / `data[n+1]` for the base legs), so handing it the walk's own shape
 * means the two paths cannot drift in interpretation, only in transport.
 *
 * A collateral the lens did not emit is filled with a zero-balance tuple, which is
 * exactly what `userCollateral` returns for an account holding none of it.
 *
 * NOT decoded with `decodeLensRows`: rows here are 18 bytes — `uint16` + a PACKED
 * `uint128` — because `UserCollateral` is `{uint128 balance; uint128 _reserved}`
 * and only the low half is a balance. The shared helper only expresses whole
 * 32-byte words, and forcing it would read the reserved half as part of the number.
 *
 * Returns `undefined` when the blob is unusable; the caller must treat that as an
 * incomplete read, never as an empty position.
 */
export const rebuildCometData = (
  blobs: unknown,
  collateralCount: number,
): any[] | undefined => {
  if (isFailedCall(blobs) || !Array.isArray(blobs) || blobs.length !== 1) {
    return undefined
  }
  const hex = blobs[0]
  if (typeof hex !== 'string' || !hex.startsWith('0x')) return undefined
  const buf = Buffer.from(hex.slice(2), 'hex')
  if (buf.length < HEADER_BYTES + 2) return undefined

  const word = (off: number) => BigInt('0x' + buf.subarray(off, off + 32).toString('hex'))
  const baseSupply = word(0)
  const baseBorrow = word(32)
  const count = buf.readUInt16BE(HEADER_BYTES)
  if (buf.length !== HEADER_BYTES + 2 + count * ROW_BYTES) return undefined

  // `userCollateral` returns a struct; viem surfaces it as a positional tuple, and
  // the parser reads `[0]`. Only the balance half is emitted, so `_reserved` — a
  // field nothing reads — is reported as 0 rather than invented.
  const out: any[] = Array.from({ length: collateralCount }, () => [0n, 0n])
  let off = HEADER_BYTES + 2
  for (let i = 0; i < count; i++) {
    const index = buf.readUInt16BE(off)
    if (index >= collateralCount) return undefined
    const balance = BigInt('0x' + buf.subarray(off + 2, off + 18).toString('hex'))
    out[index] = [balance, 0n]
    off += ROW_BYTES
  }
  out.push(baseSupply, baseBorrow)
  return out
}
